/**
 * hero-video.js
 *
 * A hero background can supply a differently-framed clip per breakpoint, so the
 * section renders up to two <video> elements (desktop + mobile). Both live in
 * the DOM at once, and `display:none` does NOT stop a browser from downloading a
 * video — with `autoplay` it eagerly fetches and decodes BOTH clips on every
 * device, doubling the transfer.
 *
 * The section renders the videos inert instead: no `autoplay` and
 * `preload="none"`, so nothing downloads on paint. This module plays only the
 * clip whose `data-hero-video` matches the active breakpoint; the off-breakpoint
 * clip is never started, so its source is never fetched. Resizing across the
 * breakpoint swaps which clip plays (and only then downloads the second).
 *
 * Decorative enhancement only — with JS disabled, no hero video plays, so a
 * section may render its pause button `hidden` and this module reveals it.
 */

import { prefersReducedMotion } from '@stardust/utilities';

// Fallback when a root carries no data-hero-desktop-query. The section picks
// its own breakpoint (a split layout switches later), and sends it that way.
const DEFAULT_DESKTOP_QUERY = '(min-width: 768px)'; // Tailwind `md`
const DEFAULT_LABEL_PAUSE = 'Pause background video';
const DEFAULT_LABEL_PLAY = 'Play background video';

/** Teardown functions for initialized roots, so a root is never wired twice. */
const cleanups = new WeakMap();

/**
 * Play the breakpoint-matching video inside a single hero section and keep the
 * others paused.
 * @param {HTMLElement} root - a `[data-hero]` section root
 */
function activate(root) {
  // The initial init() and any later shopify:section:load re-init can target
  // the same root.
  if (cleanups.has(root)) return;

  const wrappers = Array.from(root.querySelectorAll('[data-hero-video]'))
    .map((wrapper) => ({
      target: wrapper.dataset.heroVideo, // 'desktop' | 'mobile' | 'all'
      video: wrapper.querySelector('video'),
    }))
    .filter(({ video }) => video);
  if (!wrappers.length) return;

  const desktopMql = window.matchMedia(root.dataset.heroDesktopQuery || DEFAULT_DESKTOP_QUERY);
  // Everyone gets the pause control (WCAG 2.2.2); reduced motion only seeds
  // the initial paused state.
  let userPaused = prefersReducedMotion();
  const toggle = root.querySelector('[data-hero-video-toggle]');
  const pauseIcon = toggle?.querySelector('[data-hero-video-icon-pause]');
  const playIcon = toggle?.querySelector('[data-hero-video-icon-play]');
  const labelPause = toggle?.dataset.labelPause || DEFAULT_LABEL_PAUSE;
  const labelPlay = toggle?.dataset.labelPlay || DEFAULT_LABEL_PLAY;

  const matches = (target) =>
    target === 'all' ||
    (target === 'desktop' && desktopMql.matches) ||
    (target === 'mobile' && !desktopMql.matches);

  const activeVideo = () => wrappers.find(({ target }) => matches(target))?.video;

  // The button reflects what the video is actually doing, not what was asked
  // for: autoplay can be refused (iOS Low Power Mode, data saver), and a label
  // saying "Pause" over a still video would be wrong.
  const syncToggle = () => {
    if (!toggle) return;
    const video = activeVideo();
    const playing = Boolean(video && !video.paused);
    toggle.setAttribute('aria-label', playing ? labelPause : labelPlay);
    pauseIcon?.classList.toggle('hidden', !playing);
    playIcon?.classList.toggle('hidden', playing);
  };

  const apply = () => {
    wrappers.forEach(({ target, video }) => {
      if (matches(target) && !userPaused) {
        // Required for muted inline autoplay on iOS Safari when triggered via JS.
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.play().catch(syncToggle);
      } else {
        video.pause();
      }
    });
    syncToggle();
  };

  const onToggle = () => {
    const video = activeVideo();
    userPaused = Boolean(video && !video.paused);
    apply();
  };

  toggle?.addEventListener('click', onToggle);
  wrappers.forEach(({ video }) => {
    video.addEventListener('play', syncToggle);
    video.addEventListener('pause', syncToggle);
  });
  desktopMql.addEventListener('change', apply);
  if (toggle) toggle.hidden = false;
  apply();

  cleanups.set(root, () => {
    toggle?.removeEventListener('click', onToggle);
    wrappers.forEach(({ video }) => {
      video.removeEventListener('play', syncToggle);
      video.removeEventListener('pause', syncToggle);
      video.pause();
    });
    desktopMql.removeEventListener('change', apply);
    cleanups.delete(root);
  });
}

/**
 * Initialize all hero videos within a scope.
 * @param {ParentNode} [scope=document]
 */
export function initHeroVideo(scope = document) {
  scope.querySelectorAll('[data-hero]').forEach(activate);
}

/**
 * Tear down hero videos within a scope: remove listeners and pause playback.
 * @param {ParentNode} [scope=document]
 */
export function destroyHeroVideo(scope = document) {
  scope.querySelectorAll('[data-hero]').forEach((root) => cleanups.get(root)?.());
}

initHeroVideo();

// The theme editor re-renders a section on every setting change; without
// teardown each render would leave the old videos wired to the breakpoint
// listener, playing (and downloading) off-screen.
document.addEventListener('shopify:section:load', (event) => {
  initHeroVideo(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  destroyHeroVideo(event.target);
});
